import React, { useMemo } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { WhatsApp, Facebook, LinkedIn, CalendarToday, FolderOpen, ArrowRight } from '@mui/icons-material';
import { blogPosts } from '../../../data/blogPosts';
import {
  PostWrapper,
  PostHeader,
  PostCategory,
  PostTitle,
  PostMeta,
  PostHeroImage,
  PostContent,
  ShareSection,
  ShareTitle,
  ShareButtons,
  ShareButton,
  RelatedPostsSection,
  RelatedTitle,
  RelatedGrid
} from './styles';
import { BlogCard, BlogImage, BlogContent as CardContent, BlogDate, BlogCardTitle, ReadMore } from '../styles';

const BlogPost = () => {
  const { slug } = useParams();
  
  const post = useMemo(() => 
    blogPosts.find(p => p.slug === slug), 
  [slug]);

  const relatedPosts = useMemo(() => {
    if (!post) return [];
    return blogPosts
      .filter(p => p.id !== post.id && p.categorySlug === post.categorySlug)
      .slice(0, 3);
  }, [post]);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const shareUrl = window.location.href;
  const shareText = `קראו את המאמר המעניין: ${post.title}`;

  return (
    <PostWrapper>
      <Helmet>
        <title>{post.title} | אפיק לב ייעוץ משכנתאות</title>
        <meta name="description" content={post.metaDescription} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.metaDescription} />
        <meta property="og:image" content={post.image} />
        <meta property="og:url" content={shareUrl} />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <PostHeader>
          <PostCategory>{post.category}</PostCategory>
          <PostTitle>{post.title}</PostTitle>
          <PostMeta>
            <span><CalendarToday fontSize="small" /> {post.date}</span>
            <span><FolderOpen fontSize="small" /> {post.category}</span>
          </PostMeta>
        </PostHeader>

        <PostHeroImage src={post.image} />

        <PostContent dangerouslySetInnerHTML={{ __html: post.content }} />

        <ShareSection>
          <ShareTitle>אהבתם את המאמר? שתפו עם חברים</ShareTitle>
          <ShareButtons>
            <ShareButton 
              href={`https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`}
              target="_blank"
              bg="#25D366"
              aria-label="שתף בוואטסאפ"
            >
              <WhatsApp />
            </ShareButton>
            <ShareButton 
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
              target="_blank"
              bg="#1877F2"
              aria-label="שתף בפייסבוק"
            >
              <Facebook />
            </ShareButton>
            <ShareButton 
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
              target="_blank"
              bg="#0A66C2"
              aria-label="שתף בלינקדאין"
            >
              <LinkedIn />
            </ShareButton>
          </ShareButtons>
        </ShareSection>

        {relatedPosts.length > 0 && (
          <RelatedPostsSection>
            <RelatedTitle>מאמרים נוספים שעשויים לעניין אותך</RelatedTitle>
            <RelatedGrid>
              {relatedPosts.map(p => (
                <BlogCard as={Link} to={`/blog/${p.slug}`} key={p.id}>
                  <BlogImage image={p.image} fallbackImage="/images/blog/default.jpg" />
                  <CardContent>
                    <BlogDate>{p.date}</BlogDate>
                    <BlogCardTitle>{p.title}</BlogCardTitle>
                    <ReadMore>קרא עוד <ArrowRight /></ReadMore>
                  </CardContent>
                </BlogCard>
              ))}
            </RelatedGrid>
          </RelatedPostsSection>
        )}
      </motion.div>
    </PostWrapper>
  );
};

export default BlogPost;
